import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo-main.png';

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const Navbar = ({ theme, setTheme }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, loading } = useAuth();
  const userMenuRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const fn = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [userMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
    setMobileOpen(false);
  };

  const isCoursesActive =
    location.pathname === '/courses' ||
    location.pathname.startsWith('/courses/') ||
    location.pathname.startsWith('/programs');

  const displayName =
    user?.name?.trim() ||
    (user?.email ? user.email.split('@')[0] : '') ||
    'Account';
  const userInitial = (user?.name?.trim()?.[0] || user?.email?.[0] || 'A').toUpperCase();

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav className="nav" style={scrolled ? { boxShadow: '0 2px 20px rgba(0,0,0,.3)' } : {}}>
      <div className="nav-inner">
        {/* Logo */}
        <Link to="/" className="logo" onClick={closeMobile}>
          <div style={{
            background: theme === 'dark' ? '#FFFFFF' : 'transparent',
            borderRadius: theme === 'dark' ? '6px' : '0',
            padding: theme === 'dark' ? '3px 6px' : '0',
            display: 'flex', alignItems: 'center', transition: 'background 0.2s'
          }}>
            <img src={logo} alt="InvFly" style={{ height: 44, width: 'auto', objectFit: 'contain' }} />
          </div>
        </Link>

        {/* Desktop links */}
        <div className="nav-links">
          <Link to="/" className={`nl${location.pathname === '/' ? ' active' : ''}`}>Home</Link>

          <Link to="/courses" className={`nl${isCoursesActive ? ' active' : ''}`}>Courses</Link>

          <Link to="/tools" className={`nl${location.pathname === '/tools' ? ' active' : ''}`}>Tools</Link>
          <Link to="/blog" className={`nl${location.pathname === '/blog' || location.pathname.startsWith('/blog/') ? ' active' : ''}`}>Blog</Link>
          <Link to="/contact" className={`nl${location.pathname === '/contact' ? ' active' : ''}`}>Contact</Link>
          <Link to="/about" className={`nl${location.pathname === '/about' ? ' active' : ''}`}>About</Link>
        </div>

        {/* Auth / theme */}
        <div className="nav-auth">
          <button
            onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
            style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: '6px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
            title="Toggle theme"
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>

          {!loading && (
            isAuthenticated ? (
              <div style={{ position: 'relative' }} ref={userMenuRef}>
                <button className="nav-user-btn" onClick={() => setUserMenuOpen(o => !o)}>
                  <div className="nav-avatar">{userInitial}</div>
                  {displayName}
                </button>
                {userMenuOpen && (
                  <div className="nav-user-dropdown">
                    <div className="nav-user-header">
                      <div className="nav-avatar lg">{userInitial}</div>
                      <div>
                        <div className="nav-user-name">{displayName}</div>
                        {user?.email && <div className="nav-user-email">{user.email}</div>}
                      </div>
                    </div>
                    <Link to="/dashboard" className="nav-user-item" onClick={() => setUserMenuOpen(false)}>My Dashboard</Link>
                    <button className="nav-user-item logout" onClick={handleLogout}>Log out</button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/signin" className="btn-si">Sign In</Link>
                <Link to="/signup" className="btn-reg">Register</Link>
              </>
            )
          )}
        </div>

        {/* Hamburger */}
        <button className="hamburger" onClick={() => setMobileOpen(o => !o)} aria-label="Toggle menu">
          <span style={mobileOpen ? { transform: 'rotate(45deg) translate(5px,5px)' } : {}}></span>
          <span style={mobileOpen ? { opacity: 0 } : {}}></span>
          <span style={mobileOpen ? { transform: 'rotate(-45deg) translate(5px,-5px)' } : {}}></span>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {[
            { to: '/', label: 'Home' },
            { to: '/courses', label: 'Courses' },
            { to: '/tools', label: 'Tools' },
            { to: '/blog', label: 'Blog' },
            { to: '/contact', label: 'Contact' },
            { to: '/about', label: 'About' },
          ].map(({ to, label }) => (
            <Link key={to} to={to} className="nl" style={{ textAlign: 'left', justifyContent: 'flex-start' }} onClick={closeMobile}>{label}</Link>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
            {!loading && (
              isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="btn-s" style={{ fontSize: '13px', padding: '8px 16px' }} onClick={closeMobile}>Dashboard</Link>
                  <button className="btn-si" onClick={handleLogout}>Log out</button>
                </>
              ) : (
                <>
                  <Link to="/signin" className="btn-si" onClick={closeMobile}>Sign In</Link>
                  <Link to="/signup" className="btn-reg" onClick={closeMobile}>Register</Link>
                </>
              )
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
